import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, ImageOff, Loader2, PawPrint, Type } from "lucide-react";

import { DisplayCropBadge } from "./DisplayCropBadge";
import { CropOverlay } from "./CropOverlay";
import type { SceneFramingOverlay } from "./manifestFraming";
import { detectOrientationAnchor } from "./orientationLock";
import {
  sceneKindLabel,
  type SceneI2VAudit,
  type TitleDecision,
} from "./scriptStory";
import { useDisplayableImage } from "./useDisplayableImage";
import { roomBadgeLabel, roomBadgeTitle } from "./sessionUtils";
import { useMediaUrl } from "./useMediaUrl";
import type { SceneSourceMedia } from "./syncMedia";
import { orientationOf } from "./syncMedia";
import type { SyncScene, SyncTimeline } from "./syncTimeline";
import type { PortraitPersonRetakeLineage } from "./portraitPersonRetake";
import type { IdentityReport } from "./IdentityWorkbench/identityWorkbenchContracts";
import { directorViewIdentityMatches } from "./directorViewIdentity";

// ---------------------------------------------------------------------------
// SourcePanel — the right-hand pane of the Director View: the ORIGINAL
// uploaded media for the active scene, full frame at native aspect ratio
// (object-contain — never cropped; that is the whole point), with badges for
// scene identity, source dims/orientation, the active window, and the timeline
// confidence. HEIC originals go through useDisplayableImage (converted view)
// while "Open original" links the raw signed bytes for pixel-peeping.
// ---------------------------------------------------------------------------

function formatClock(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds - mins * 60;
  return `${mins}:${secs.toFixed(1).padStart(4, "0")}`;
}

const CONFIDENCE_LABEL: Record<SyncTimeline["source"], string> = {
  manifest_realized: "realized · exact",
  contract_beats: "beats",
  scene_timeline: "planned · estimated",
  storyboard_estimate: "storyboard · estimated",
  manual: "manual",
};

export function timelineSourceLabel(timeline: SyncTimeline): string {
  if (timeline.source === "contract_beats") {
    return timeline.confidence === "good"
      ? "beats · good"
      : "beats · estimated";
  }
  return CONFIDENCE_LABEL[timeline.source];
}

export function formatVisualBreedKey(value: string): string {
  return value
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function visualBreedLabel(
  observation: IdentityReport["subjects"][number]["breed_observation"],
): string {
  if (observation?.status === "observed" && observation.breed_key) {
    return `Likely visual breed: ${formatVisualBreedKey(observation.breed_key)}`;
  }
  if (observation?.status === "abstained") return "Visual breed not determined";
  if (
    observation?.status === "failed" ||
    observation?.status === "unavailable"
  ) {
    return "Visual breed unavailable for this Echo";
  }
  return "Breed not evaluated";
}

/**
 * Realized-minus-planned drift at the scene's end, in seconds. Only meaningful
 * on the exact tier (realized bounds) with planned bounds to compare against.
 */
export function sceneDriftSeconds(scene: SyncScene | null): number | null {
  if (
    !scene ||
    scene.skipped ||
    scene.endSeconds == null ||
    scene.plannedEndSeconds == null
  ) {
    return null;
  }
  const drift = scene.endSeconds - scene.plannedEndSeconds;
  return Math.abs(drift) < 0.005 ? 0 : drift;
}

/**
 * Invisible warmer for a neighbouring scene's original: mounting the hook
 * signs the URL and (for HEIC) runs the heic2any conversion into the module
 * cache, so stepping to the next scene is instant.
 */
export function SourcePrefetch({
  source,
}: {
  source: SceneSourceMedia | null;
}) {
  const media =
    source && (source.kind === "image" || source.kind === "video")
      ? source.media
      : null;
  const image = useDisplayableImage(media ?? null);
  useEffect(() => {
    if (!image.url || typeof Image === "undefined") return;
    const warm = new Image();
    warm.decoding = "async";
    warm.src = image.url;
  }, [image.url]);
  return null;
}

export function SourcePanel({
  scene,
  source,
  isAdmin,
  showDrift,
  framing,
  overlayEnabled,
  fullBleed,
  mediaMaxHeightClassName,
  titleDecision,
  portraitRetake,
  identityReport,
  identityMode,
  i2v,
  identityReferenceBadge,
}: {
  scene: SyncScene | null;
  source: SceneSourceMedia | null;
  isAdmin?: boolean;
  /** Tier 1 only: show the realized-vs-planned Δ badge (QCSV-006). */
  showDrift?: boolean;
  /** Manifest framing data for this scene (QCSV-007), when telemetry is active. */
  framing?: SceneFramingOverlay | null;
  /** Admin toggle state: draw the crop overlay over the original (QCSV-008). */
  overlayEnabled?: boolean;
  /** Stage context (inline or fullscreen): size media to the pane instead of 70vh. */
  fullBleed?: boolean;
  /**
   * Viewport cap for the media when the stage's chrome differs from the
   * fullscreen dialog's (the inline stage sits under the app header and card
   * header). Defaults to the dialog's cap under `fullBleed`.
   */
  mediaMaxHeightClassName?: string;
  /** Title-card debugging: the echo's title decision (rationale etc.). */
  titleDecision?: TitleDecision | null;
  /** Accepted per-image 4:3 derivation evidence from the orchestrator event. */
  portraitRetake?: PortraitPersonRetakeLineage | null;
  identityReport?: IdentityReport | null;
  identityMode?: string | null;
  /** The active scene's I2V audit, for the frozen-position (orientation lock) badge. */
  i2v?: SceneI2VAudit | null;
  /**
   * The active scene's multi-reference badge (DCR-002), passed in rather than
   * built here: this panel stays presentational, and the identity-reference
   * hover card keeps its dependencies out of a component this widely rendered.
   */
  identityReferenceBadge?: React.ReactNode;
}) {
  const media =
    source && (source.kind === "image" || source.kind === "video")
      ? source.media
      : null;
  const image = useDisplayableImage(source?.kind === "image" ? media : null);
  const rawUrl = useMediaUrl(media ?? undefined);
  const videoUrl = useMediaUrl(source?.kind === "video" ? media : undefined);
  const [naturalDims, setNaturalDims] = useState<{
    width: number;
    height: number;
  } | null>(null);
  // One automatic signed-URL refresh per scene on load error, then placeholder.
  const retriedRef = useRef<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const sourceVideoRef = useRef<HTMLVideoElement | null>(null);
  const [sourceTimeUs, setSourceTimeUs] = useState<number | null>(null);

  const storageKey =
    source && (source.kind === "image" || source.kind === "video")
      ? source.storageKey
      : null;

  useEffect(() => {
    setNaturalDims(null);
    setFailed(null);
    setSourceTimeUs(null);
  }, [storageKey]);

  if (!scene) {
    return (
      <div className="flex h-full min-h-48 items-center justify-center rounded-lg border bg-muted/30 text-sm text-muted-foreground">
        Logo outro — no source media
      </div>
    );
  }

  const width =
    (source?.kind === "image" || source?.kind === "video"
      ? source.width
      : null) ??
    naturalDims?.width ??
    null;
  const height =
    (source?.kind === "image" || source?.kind === "video"
      ? source.height
      : null) ??
    naturalDims?.height ??
    null;
  const orientation = orientationOf(width, height);
  const identityMatches = directorViewIdentityMatches(
    identityReport ?? null,
    source && (source.kind === "image" || source.kind === "video")
      ? source.sourceMediaId
      : null,
    source?.kind === "video" ? sourceTimeUs : null,
  );

  const handleMediaError = (refresh: () => void) => {
    if (!storageKey) return;
    if (retriedRef.current !== storageKey) {
      retriedRef.current = storageKey;
      refresh();
      return;
    }
    setFailed(storageKey);
  };

  const badges: React.ReactNode[] = [];
  badges.push(
    <Badge key="scene" variant="secondary">
      Scene {scene.sceneId ?? scene.index + 1}
    </Badge>,
    <Badge key="kind" variant="outline">
      {sceneKindLabel(scene.sceneType)}
    </Badge>,
  );
  if (identityReferenceBadge) {
    // Sits with the scene's own badges, so watching the film in fullscreen
    // shows which shots went multi-reference as they play — hover names the
    // cast subject and the photos that rode along to the video model.
    badges.push(
      <span key="identity-references">{identityReferenceBadge}</span>,
    );
  }
  if (scene.fallback) {
    badges.push(
      <Badge
        key="fallback"
        variant="outline"
        className="border-amber-500 text-amber-600"
      >
        fallback asset
      </Badge>,
    );
  }
  if (scene.skipped) {
    badges.push(
      <Badge key="skipped" variant="destructive">
        skipped in final cut
      </Badge>,
    );
  }
  if (
    (source?.kind === "image" || source?.kind === "video") &&
    source.framingIntent
  ) {
    badges.push(
      <Badge key="intent" variant="outline">
        intent: {source.framingIntent}
      </Badge>,
    );
  }
  // Room map (FPR-033): which room the director skill mapped this photo to,
  // and — on a `floor_plan.v2` multi-level plan (FPV-031) — the level it sits
  // on in parentheses ("Kitchen (Planta baja)"). Mirrors the frozen-position
  // badge: hover carries the level, category, room id and confidence so a
  // wrong assignment is diagnosable from the film.
  const roomLabel =
    source?.kind === "image" || source?.kind === "video"
      ? roomBadgeLabel(source.room)
      : null;
  if (roomLabel && (source?.kind === "image" || source?.kind === "video")) {
    badges.push(
      <Badge
        key="room"
        data-testid="room-scene-badge"
        variant="outline"
        className="border-violet-500 text-violet-600"
        title={roomBadgeTitle(source.room)}
      >
        {roomLabel}
      </Badge>,
    );
  }
  const orientationAnchor = detectOrientationAnchor(i2v?.finalPositivePrompt);
  if (orientationAnchor) {
    badges.push(
      <Badge
        key="frozen-position"
        data-testid="frozen-position-scene-badge"
        variant="outline"
        className="border-sky-500 text-sky-600"
        title={`Orientation lock triggered (prompt bundle v5): the rendered direction anchors the captured facing — "${orientationAnchor}"`}
      >
        frozen position
      </Badge>,
    );
  }
  if (portraitRetake) {
    const qcSummary =
      portraitRetake.qc === "identity"
        ? "identity QC passed"
        : `subject QC passed (${portraitRetake.subjectLabels.join(", ")})`;
    badges.push(
      <Badge
        key="portrait-retake"
        variant="outline"
        className="border-emerald-500 text-emerald-600"
        title={`${portraitRetake.provider} - ${portraitRetake.promptVersion} - ${qcSummary} - ${portraitRetake.outputWidth}x${portraitRetake.outputHeight}`}
      >
        Normalized to 4:3 - {portraitRetake.modelId}
      </Badge>,
    );
  }
  if (
    isAdmin &&
    (source?.kind === "image" || source?.kind === "video") &&
    source.matchedBy !== "storage_key"
  ) {
    badges.push(
      <Badge key="match" variant="outline" className="text-muted-foreground">
        matched by {source.matchedBy === "basename" ? "filename" : "i2v frame"}
      </Badge>,
    );
  }
  // Tier-1 drift badge (QCSV-006): realized vs planned end — a QC signal in
  // itself (positive = the scene ran long, e.g. beat-snap stretch).
  const drift = showDrift ? sceneDriftSeconds(scene) : null;
  if (drift != null) {
    badges.push(
      <Badge
        key="drift"
        variant="outline"
        className={
          Math.abs(drift) > 0.5 ? "border-amber-500 text-amber-600" : ""
        }
        title="Realized minus planned scene end (beat snap, conform, skips)"
      >
        Δ {drift >= 0 ? "+" : ""}
        {drift.toFixed(2)} s
      </Badge>,
    );
  }

  // Framing chips (QCSV-008): shown whenever framing data exists so operators
  // see the verdict even before toggling the overlay on.
  if (framing) {
    if (framing.strategy) {
      badges.push(
        <Badge key="strategy" variant="outline">
          {framing.strategy.replace(/_/g, " ")}
        </Badge>,
      );
    }
    if (framing.retainedAreaFraction != null) {
      badges.push(
        <Badge key="retained" variant="outline">
          kept {(framing.retainedAreaFraction * 100).toFixed(0)} %
        </Badge>,
      );
    }
    if (framing.fallbackReason) {
      badges.push(
        <Badge
          key="framing-fallback"
          variant="outline"
          className="border-amber-500 text-amber-600"
        >
          {framing.fallbackReason.replace(/_/g, " ")}
        </Badge>,
      );
    }
    if (framing.hasCropPath) {
      badges.push(
        <Badge
          key="moving"
          variant="outline"
          title="Ken Burns / tracking: the crop window animates; the overlay draws the base window"
        >
          moving crop
        </Badge>,
      );
    }
    if (framing.appliedStatus && framing.appliedStatus !== "applied") {
      badges.push(
        <Badge
          key="applied-status"
          variant="outline"
          className="border-amber-500 text-amber-600"
        >
          receipt: {framing.appliedStatus}
        </Badge>,
      );
    }
  }

  const metaLine: string[] = [];
  if (source?.kind === "image" || source?.kind === "video") {
    if (source.sourceFile) metaLine.push(source.sourceFile);
    if (width && height) metaLine.push(`${width}×${height}`);
    if (orientation) metaLine.push(orientation);
  }
  if (scene.startSeconds != null && scene.endSeconds != null) {
    metaLine.push(
      `window ${formatClock(scene.startSeconds)}–${formatClock(scene.endSeconds)}`,
    );
  }

  // Viewport-unit caps (not %-of-parent): the img sits in a shrink-wrapped
  // relative box (so the crop overlay tracks its exact box), where percentage
  // max-height would be circular and silently ignored. 14rem ≈ dialog chrome
  // (padding + strip + badge/meta rows).
  const mediaMaxHeight =
    mediaMaxHeightClassName ??
    (fullBleed ? "max-h-[calc(100dvh-14rem)]" : "max-h-[70vh]");
  const mediaClass = `${mediaMaxHeight} max-w-full object-contain`;

  let body: React.ReactNode;
  if (!source || source.kind === "missing") {
    body = (
      <Placeholder icon={<ImageOff className="size-6" />}>
        {source?.sourceFile
          ? `Original ${source.sourceFile} is not available (expired or erased).`
          : "No source media for this scene."}
      </Placeholder>
    );
  } else if (source.kind === "title") {
    body = (
      <div className="flex h-full min-h-48 flex-col items-center justify-center gap-3 overflow-y-auto rounded-lg bg-black p-6 text-center text-white">
        <Type className="size-5 opacity-60" />
        <div className="max-w-md text-balance text-xl font-semibold leading-snug">
          {source.titleText ?? "Title card"}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          {source.cardType ? (
            <Badge variant="outline" className="border-white/30 text-white/80">
              {source.cardType}
            </Badge>
          ) : null}
          {titleDecision?.selected
            .filter((cardType) => cardType !== source.cardType)
            .map((cardType) => (
              <Badge
                key={cardType}
                variant="outline"
                className="border-white/20 text-white/60"
              >
                also: {cardType}
              </Badge>
            ))}
          {titleDecision?.followedRecommendation === false ? (
            <Badge
              variant="outline"
              className="border-amber-400/70 text-amber-300"
            >
              outside recommendation
            </Badge>
          ) : null}
        </div>
        {titleDecision?.rationale ? (
          <p className="max-w-md text-left text-xs leading-relaxed text-white/70">
            <span className="font-medium text-white/90">Why this title: </span>
            {titleDecision.rationale}
          </p>
        ) : null}
        {titleDecision?.mismatchReason ? (
          <p className="max-w-md text-left text-xs leading-relaxed text-amber-300/90">
            {titleDecision.mismatchReason}
          </p>
        ) : null}
      </div>
    );
  } else if (failed === storageKey) {
    body = (
      <Placeholder icon={<ImageOff className="size-6" />}>
        Media unavailable (expired or erased).
        {rawUrl.url ? (
          <a
            className="ml-1 inline-flex items-center gap-1 underline"
            href={rawUrl.url}
            target="_blank"
            rel="noreferrer"
          >
            Try the raw file <ExternalLink className="size-3" />
          </a>
        ) : null}
      </Placeholder>
    );
  } else if (source.kind === "video") {
    body = videoUrl.url ? (
      <div
        className={`flex items-center justify-center ${fullBleed ? "h-full" : ""}`}
      >
        <video
          ref={sourceVideoRef}
          src={videoUrl.url}
          muted
          playsInline
          controls
          autoPlay
          loop
          onError={() => handleMediaError(videoUrl.refresh)}
          onLoadedMetadata={(event) => {
            const element = event.currentTarget;
            setNaturalDims({
              width: element.videoWidth,
              height: element.videoHeight,
            });
            if (source.trimStartSeconds != null) {
              element.currentTime = source.trimStartSeconds;
            }
            setSourceTimeUs(Math.round(element.currentTime * 1_000_000));
          }}
          onTimeUpdate={(event) =>
            setSourceTimeUs(
              Math.round(event.currentTarget.currentTime * 1_000_000),
            )
          }
          className={`bg-black ${mediaClass}`}
        />
      </div>
    ) : (
      <Placeholder icon={<Loader2 className="size-6 animate-spin" />}>
        Signing the source video…
      </Placeholder>
    );
  } else if (image.url) {
    body = (
      <div
        className={`flex items-center justify-center ${fullBleed ? "h-full" : ""}`}
      >
        <div className="relative">
          <img
            src={image.url}
            alt={source.sourceFile ?? "Original photo"}
            onError={() => handleMediaError(image.refresh)}
            onLoad={(event) => {
              const element = event.currentTarget;
              if (element.naturalWidth && element.naturalHeight) {
                setNaturalDims({
                  width: element.naturalWidth,
                  height: element.naturalHeight,
                });
              }
            }}
            className={`bg-black/90 ${mediaClass}`}
          />
          <DisplayCropBadge
            label="Full frame · contain"
            isDisplayCrop={false}
          />
          {overlayEnabled && framing ? (
            <CropOverlay
              framing={framing}
              naturalWidth={naturalDims?.width ?? null}
              naturalHeight={naturalDims?.height ?? null}
            />
          ) : null}
        </div>
      </div>
    );
  } else if (image.status === "error") {
    body = (
      <Placeholder icon={<ImageOff className="size-6" />}>
        {image.error ?? "Could not display this original."}
        {rawUrl.url ? (
          <a
            className="ml-1 inline-flex items-center gap-1 underline"
            href={rawUrl.url}
            target="_blank"
            rel="noreferrer"
          >
            Open raw file <ExternalLink className="size-3" />
          </a>
        ) : null}
      </Placeholder>
    );
  } else {
    body = (
      <Placeholder icon={<Loader2 className="size-6 animate-spin" />}>
        {image.isLoading ? "Decoding the original…" : "Signing the original…"}
      </Placeholder>
    );
  }

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="min-h-0 flex-1 overflow-hidden rounded-lg">{body}</div>
      {identityMatches.length > 0 ? (
        <div
          className="rounded-lg border border-sky-200 bg-sky-50/70 p-3 text-sm dark:border-sky-900 dark:bg-sky-950/30"
          data-testid="director-view-dog-identity"
        >
          <div className="flex flex-wrap items-center gap-2">
            <PawPrint className="size-4 text-sky-700 dark:text-sky-300" />
            <span className="font-medium">Dog identity</span>
            {identityMode === "observe" ? (
              <Badge variant="outline">Observe only</Badge>
            ) : null}
          </div>
          <div className="mt-2 space-y-2">
            {identityMatches.map((match) => (
              <div
                key={match.identityId ?? "unknown"}
                className="flex flex-wrap items-center gap-x-2 gap-y-1"
              >
                <span className="font-medium">{match.label}</span>
                <Badge variant={match.unknown ? "secondary" : "outline"}>
                  {match.confidence === "unknown"
                    ? "Confidence unavailable"
                    : `${match.confidence} confidence`}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {match.resolutionStatus.replaceAll("_", " ")}
                  {match.detectionCount > 0
                    ? ` · ${match.detectionCount} detection${match.detectionCount === 1 ? "" : "s"} here`
                    : ""}
                  {match.supportEvidenceCount > 0
                    ? ` · ${match.supportEvidenceCount} supporting observation${match.supportEvidenceCount === 1 ? "" : "s"}`
                    : ""}
                </span>
                <span className="w-full text-xs font-medium text-sky-800 dark:text-sky-200">
                  {match.unknown
                    ? "Visual breed not determined"
                    : visualBreedLabel(match.breedObservation)}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            Species: dog · Visual classification only; not pedigree evidence.
          </div>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5">{badges}</div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {metaLine.map((item) => (
          <span key={item}>{item}</span>
        ))}
        {source?.kind === "video" &&
        source.trimStartSeconds != null &&
        source.trimEndSeconds != null ? (
          <span>
            video source · trimmed {source.trimStartSeconds.toFixed(1)}–
            {source.trimEndSeconds.toFixed(1)} s
          </span>
        ) : null}
        {rawUrl.url &&
        (source?.kind === "image" || source?.kind === "video") ? (
          <a
            href={rawUrl.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
          >
            Open original <ExternalLink className="size-3" />
          </a>
        ) : null}
      </div>
    </div>
  );
}

function Placeholder({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex h-full min-h-48 flex-col items-center justify-center gap-2 rounded-lg border bg-muted/30 p-4 text-center text-sm text-muted-foreground">
      {icon}
      <div>{children}</div>
    </div>
  );
}
